"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { History, Layers, Sparkles } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/features/insider-connections";
const APP = "/insider-connection";

// ============================================================================
// HERO TABS
// ============================================================================

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "cold-email",
    name: "Cold Email",
    title: "Emails Hiring Managers Answer",
    description:
      "Enter the role and company and upload your resume. You get a short, specific email with one proof point and one clear ask, ready to send from the app.",
    imageSrcUrl: `${IMG}/hero-cold-email.png`,
    primaryActionText: "Write a Cold Email",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "linkedin-dm",
    name: "LinkedIn DM",
    title: "DMs Short Enough to Get Read",
    description:
      "Choose what you want from the message: an interview, a connection, a wider network or a follow-up. The DM is written to fit a LinkedIn connection note.",
    imageSrcUrl: `${IMG}/hero-linkedin-dm.png`,
    primaryActionText: "Write a LinkedIn DM",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "email-finder",
    name: "Email Finder",
    title: "Find the Right Inbox",
    description:
      "Type a name and company domain to find a work email, check whether it is verified, and start writing to that person on the same screen.",
    imageSrcUrl: `${IMG}/hero-email-finder.png`,
    primaryActionText: "Find an Email",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

// ============================================================================
// FEATURE GROUP 1 (dark)
// ============================================================================

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "personalized-outreach",
    title: "Built From Your Resume and Their Role",
    subtitle: "YOUR PROOF, THEIR PRIORITY",
    description:
      "Each message pairs one result from your resume with one priority from the job description. The reader sees why you are relevant in the first two lines.",
    benefits: [
      "Achievements pulled from your resume",
      "Priorities pulled from the posting",
      "One clear, small ask",
      "Under 150 words",
    ],
    primaryCTA: "Write My Message",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/personalized-outreach.png`,
    icon: <Sparkles className="w-4 h-4" />,
    stats: "Personal in two lines",
  },
  {
    id: "dm-goals",
    title: "Four Goals, Four Kinds of Message",
    subtitle: "THE ASK CHANGES THE MESSAGE",
    description:
      "Asking for an interview is different from saying hello to a peer. Pick your goal and the tone, length and ask adjust to match it.",
    benefits: [
      "Ask for an interview",
      "Build industry connections",
      "Expand your network",
      "Follow up after an application",
    ],
    primaryCTA: "Choose a Goal",
    primaryLink: APP,
    secondaryCTA: "Find Jobs First",
    secondaryLink: "/features/ai-job-match",
    imageSrcUrl: `${IMG}/dm-goals.png`,
    icon: <Layers className="w-4 h-4" />,
    stats: "4 LinkedIn DM goals",
    orientation: "reversed",
  },
];

// ============================================================================
// FEATURE GROUP 2 (light)
// ============================================================================

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "message-history",
    title: "Every Message, Saved",
    subtitle: "PICK UP WHERE YOU LEFT OFF",
    description:
      "Message History keeps every email and DM you generate, with the recipient, role and status, so a good message is never lost and follow-ups are easy.",
    benefits: [
      "Filter by email or LinkedIn",
      "Recipient and role on every entry",
      "Copy or send again",
      "Daily generations shown upfront",
    ],
    primaryCTA: "Open Network AI",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/message-history.png`,
    icon: <History className="w-4 h-4" />,
    stats: "Email + LinkedIn history",
  },
];

// ============================================================================
// TESTIMONIALS (replace with real user quotes before publishing)
// ============================================================================

const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "My applications were going nowhere. One email to the engineering manager, with the latency number from my resume in the second line, got me a call the next day.",
    name: "Arjun M.",
    role: "Backend Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "I used to send 'I'd like to add you to my network' and hear nothing. The interview DM referenced her post and asked for ten minutes. She said yes.",
    name: "Kavya R.",
    role: "Product Marketing Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "Email Finder saved me from guessing addresses. Finding the verified email and writing the message on one screen took two minutes.",
    name: "Rohit D.",
    role: "Financial Analyst",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=800&fit=crop&q=80",
  },
];

// ============================================================================
// PAGE
// ============================================================================

export default function NetworkAIPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Reach the People",
            titleHighlight: "Who Hire.",
            subtitle:
              "A specific message to the right person can move your application to the top of the pile. Insider Connections writes cold emails and LinkedIn DMs from your resume and the job, and finds the email to send them to.",
            ctaText: "Start Reaching Out",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Messages Worth Replying To.",
            sectionSubtitle:
              "Short, specific and written for the person reading them.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Replies, Not Silence",
            subtitle: "From job seekers who went straight to the hiring team",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Insider Connections",
            subtitle: "How outreach works and what you can send.",
            faqs,
            ctaTitle: "Ready to message a hiring manager?",
            ctaText: "Start Reaching Out",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
